import { drawRaster, makeWorldCanvas } from '@/engine/canvas';
import { ctxOf } from '@/engine/canvas';
import { TILE, floorTile, wallFace, wallTop, wallCap, floorShadow, stairsTile } from '@/gfx/tiles';
import { Painter, flipH, silhouette } from '@/gfx/pixel';
import { C } from '@/gfx/palette';
import { propSprite, decorSprite } from '@/world/props';
import { FLOOR_KINDS, idx, type Floor, type DoorEnt, type PropEnt, type StairsEnt } from '@/world/builder';
import { T_VOID, T_WALL, T_FLOOR, T_DOOR, T_STAIRS } from '@/world/types';

const terrainCache = new WeakMap<Floor, HTMLCanvasElement>();
const residentFloors = new Set<Floor>();
const shadowCache = new WeakMap<HTMLCanvasElement, HTMLCanvasElement>();
/** The floor's ground raster (tiles, wall faces, decor, flat props, contact shadows), rebuilt when marked dirty. */
export function terrainCanvas(f: Floor): HTMLCanvasElement {
  const c = terrainCache.get(f);
  return c && !f.staticDirty ? c : renderStatic(f);
}

/**
 * Prerender everything that doesn't move. Upright furniture is not baked in: the viewport
 * draws it every frame anyway, and the map composes it on demand (see drawFurnished), so a
 * floor keeps one 2x raster instead of two.
 */
export function renderStatic(f: Floor): HTMLCanvasElement {
  // Retain at most two large 2x floor rasters. Exploration masks are small and persist.
  residentFloors.delete(f); residentFloors.add(f);
  while (residentFloors.size > 2) {
    const old = residentFloors.values().next().value!;
    residentFloors.delete(old); terrainCache.delete(old);
    old.staticDirty = true;
  }
  const c = terrainCache.get(f) ?? makeWorldCanvas(f.w * TILE, f.h * TILE);
  const ctx = ctxOf(c);
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, c.width, c.height);
  for (let y = 0; y < f.h; y++) {
    for (let x = 0; x < f.w; x++) {
      const i = idx(f, x, y);
      const t = f.tiles[i];
      const px = x * TILE, py = y * TILE;
      if (t === T_VOID) continue;
      if (t === T_WALL) {
        const below = y + 1 < f.h ? f.tiles[idx(f, x, y + 1)] : T_VOID;
        if (below === T_FLOOR || below === T_DOOR || below === T_STAIRS) {
          const ri = f.roomIdx[idx(f, x, y + 1)];
          const style = ri >= 0 ? f.rooms[ri].wall : 'plaster';
          drawRaster(ctx, wallFace(style), px, py);
        } else {
          drawRaster(ctx, wallTop(), px, py);
          const below2 = y + 2 < f.h ? f.tiles[idx(f, x, y + 2)] : T_VOID;
          if (below === T_WALL && (below2 === T_FLOOR || below2 === T_DOOR || below2 === T_STAIRS)) drawRaster(ctx, wallCap(), px, py);
        }
        continue;
      }
      if (t === T_DOOR) {
        const d = f.doorAt.get(i)!;
        if (d.kind === 'secret' && !d.revealed) {
          const below = y + 1 < f.h ? f.tiles[idx(f, x, y + 1)] : T_VOID;
          if (d.orient === 'h' && (below === T_FLOOR || below === T_DOOR || below === T_STAIRS)) {
            const ri = f.roomIdx[idx(f, x, y + 1)];
            drawRaster(ctx, wallFace(ri >= 0 ? f.rooms[ri].wall : 'plaster'), px, py);
          } else drawRaster(ctx, wallTop(), px, py);
          continue;
        }
      }
      if (t === T_STAIRS) {
        const s = f.stairsAt.get(i)!;
        const row = s.def.enter === 'S' ? s.y + s.h - 1 - y : s.def.enter === 'N' ? y - s.y : 0;
        drawRaster(ctx, stairsTile(s.def.dir, row), px, py);
        if (s.def.kind === 'ladder') drawRaster(ctx, ladderSprite(), px, py - 16);
        continue;
      }
      const kind = FLOOR_KINDS[f.floorKind[i]];
      drawRaster(ctx, floorTile(kind, f.variant[i]), px, py);
      // ambient occlusion under north walls
      if (y > 0 && f.tiles[idx(f, x, y - 1)] === T_WALL) drawRaster(ctx, floorShadow(), px, py);
      // side shadow along west walls
      if (x > 0 && f.tiles[idx(f, x - 1, y)] === T_WALL) {
        ctx.fillStyle = 'rgba(0,0,0,0.18)';
        ctx.fillRect(px, py, 2, TILE);
      }
    }
  }
  // Inlaid room edges give the architecture a clear silhouette without noisy floor tiles.
  for (const room of f.rooms) {
    if (!room.floor.startsWith('carpet') && room.floor !== 'marble') continue;
    const x = room.x * TILE, y = room.y * TILE, w = room.w * TILE, h = room.h * TILE;
    ctx.strokeStyle = room.floor === 'marble' ? C.woodPaleDk : C.carpetRedDk;
    ctx.lineWidth = 1;
    ctx.strokeRect(x + 4.5, y + 4.5, w - 9, h - 9);
    ctx.strokeStyle = room.floor === 'marble' ? C.sheetSh : C.woodPaleDk;
    ctx.globalAlpha = 0.45;
    ctx.strokeRect(x + 6.5, y + 6.5, w - 13, h - 13);
    ctx.globalAlpha = 1;
  }
  // stairs railings for wide stairs
  for (const s of f.stairs) drawStairsFrame(ctx, s);
  // decor on wall faces
  for (const d of f.decor) drawRaster(ctx, decorSprite(d.kind, d.variant), d.x * TILE, d.y * TILE);
  // flat props
  for (const p of f.props) if (p.kind.flat) drawProp(ctx, p, 0, 0);
  // Contact shadows are part of the ground, so actors can pass over them.
  for (const p of f.props) {
    if (p.kind.flat) continue;
    const spr = propSprite(p.kind);
    let shadow = shadowCache.get(spr);
    if (!shadow) { shadow = silhouette(spr, '#090b17'); shadowCache.set(spr, shadow); }
    ctx.save();
    ctx.globalAlpha = 0.23;
    drawRaster(ctx, shadow, p.x * TILE + 3, (p.y + p.kind.fh) * TILE - 5, p.kind.sw, Math.max(5, p.kind.sh * 0.22));
    ctx.globalAlpha = 0.22;
    ctx.fillStyle = '#080811';
    ctx.fillRect(p.x * TILE + 2, (p.y + p.kind.fh) * TILE - 2, p.kind.sw - 4, 3);
    ctx.restore();
  }
  terrainCache.set(f, c);
  f.staticDirty = false;
  return c;
}

/**
 * Draw a region of the furnished floor (ground plus y-sorted upright furniture) scaled into a
 * destination rectangle, for the map and the overview. `src` is in world pixels.
 */
export function drawFurnished(ctx: CanvasRenderingContext2D, f: Floor, src: { x: number; y: number; w: number; h: number }, dx: number, dy: number, dw: number, dh: number): void {
  const sx = dw / src.w, sy = dh / src.h;
  drawRaster(ctx, terrainCanvas(f), src.x, src.y, src.w, src.h, dx, dy, dw, dh);
  ctx.save();
  ctx.beginPath(); ctx.rect(dx, dy, dw, dh); ctx.clip();
  const rest = f.props.filter((p) => !p.kind.flat).sort((a, b) => a.y + a.kind.fh - (b.y + b.kind.fh) || a.x - b.x);
  for (const p of rest) {
    const r = propScreenRect(p);
    if (r.x + r.w <= src.x || r.x >= src.x + src.w || r.y + r.h <= src.y || r.y >= src.y + src.h) continue;
    drawRaster(ctx, propImage(p), dx + (r.x - src.x) * sx, dy + (r.y - src.y) * sy, r.w * sx, r.h * sy);
  }
  ctx.restore();
}

const flipCache = new WeakMap<HTMLCanvasElement, HTMLCanvasElement>();
export function propImage(p: PropEnt): HTMLCanvasElement {
  let spr = propSprite(p.kind);
  if (p.flip) {
    let fl = flipCache.get(spr);
    if (!fl) {
      fl = flipH(spr);
      flipCache.set(spr, fl);
    }
    spr = fl;
  }
  return spr;
}
export function drawProp(ctx: CanvasRenderingContext2D, p: PropEnt, offX: number, offY: number): void {
  const spr = propImage(p);
  const px = p.x * TILE - offX;
  const py = (p.y + p.kind.fh) * TILE - p.kind.sh - offY;
  drawRaster(ctx, spr, px, py);
}

export function propScreenRect(p: PropEnt): { x: number; y: number; w: number; h: number } {
  return { x: p.x * TILE, y: (p.y + p.kind.fh) * TILE - p.kind.sh, w: p.kind.sw, h: p.kind.sh };
}

function drawStairsFrame(ctx: CanvasRenderingContext2D, s: StairsEnt): void {
  if (s.def.kind === 'ladder') return;
  const x = s.x * TILE, y = s.y * TILE, w = s.w * TILE, h = s.h * TILE;
  ctx.fillStyle = C.panelDk;
  if (s.def.enter === 'S' || s.def.enter === 'N') {
    ctx.fillRect(x, y, 2, h);
    ctx.fillRect(x + w - 2, y, 2, h);
    ctx.fillStyle = C.brass;
    ctx.fillRect(x, y, 2, 1);
    ctx.fillRect(x + w - 2, y, 2, 1);
  } else {
    ctx.fillRect(x, y, w, 2);
    ctx.fillRect(x, y + h - 2, w, 2);
  }
}

let ladder: HTMLCanvasElement | null = null;
function ladderSprite(): HTMLCanvasElement {
  if (ladder) return ladder;
  const p = new Painter(16, 32);
  p.rect(3, 0, 2, 32, C.woodPaleDk).rect(11, 0, 2, 32, C.woodPaleDk);
  for (let y = 2; y < 32; y += 5) p.rect(3, y, 10, 2, C.woodPale);
  ladder = p.done();
  return ladder;
}

// ---------- doors ----------
const doorCache = new Map<string, HTMLCanvasElement>();
export function doorSprite(d: DoorEnt, openState: boolean): HTMLCanvasElement {
  const key = `${d.kind}:${d.orient}:${d.w}:${openState ? 'o' : 'c'}:${d.revealed ? 'r' : 'h'}:${d.locked ? 'l' : 'u'}`;
  let c = doorCache.get(key);
  if (c) return c;
  c = drawDoor(d, openState);
  doorCache.set(key, c);
  return c;
}

function drawDoor(d: DoorEnt, open: boolean): HTMLCanvasElement {
  const O = C.outline;
  if (d.orient === 'h') {
    const w = d.w * TILE;
    const p = new Painter(w, 20);
    if (d.kind === 'open' || d.kind === 'arch') {
      // The opening is terrain behind the actors; only its frame is foreground.
      p.rect(0, 0, 2, 16, C.panelLt).rect(w - 2, 0, 2, 16, C.panelLt).rect(0, 0, w, 2, C.panelLt);
      return p.done();
    }
    if (d.kind === 'secret' && !d.revealed) {
      // rendered as wall; nothing
      return p.done();
    }
    if (open) {
      p.rect(0, 0, 2, 16, C.panelLt).rect(w - 2, 0, 2, 16, C.panelLt).rect(0, 0, w, 2, C.panelLt);
      // door leaf swung inward (drawn at the side)
      p.panel(1, 4, 5, 16, C.panel, O, C.panelLt, C.panelDk);
      if (d.w === 2) p.panel(w - 6, 4, 5, 16, C.panel, O, C.panelLt, C.panelDk);
      return p.done();
    }
    // closed leaf
    const leafW = d.w === 2 ? w / 2 : w;
    for (let k = 0; k < d.w; k++) {
      const x = k * leafW;
      if (d.kind === 'cell' || d.kind === 'gate') {
        p.rect(x, 0, leafW, 16, '#0b0810');
        for (let bx = x + 2; bx < x + leafW; bx += 4) p.rect(bx, 0, 2, 16, C.bars).px(bx, 0, C.barsLt);
        p.rect(x, 7, leafW, 2, C.barsLt);
        if (d.kind === 'gate') p.rect(x + leafW / 2 - 2, 5, 4, 5, C.metalDk).px(x + leafW / 2 - 1, 7, C.fire);
      } else {
        p.panel(x, 0, leafW, 16, C.panel, O, C.panelLt, C.panelDk);
        p.box(x + 3, 2, leafW - 6, 5, C.panelDk).box(x + 3, 9, leafW - 6, 5, C.panelDk);
        p.rect(x + (k === 0 && d.w === 2 ? leafW - 4 : 2), 8, 2, 2, C.brass);
        if (d.kind === 'boarded') {
          p.rect(x + 1, 3, leafW - 2, 3, C.woodPale).box(x + 1, 3, leafW - 2, 3, O);
          p.rect(x + 1, 10, leafW - 2, 3, C.woodPale).box(x + 1, 10, leafW - 2, 3, O);
          p.px(x + 2, 4, C.metalDk).px(x + leafW - 3, 4, C.metalDk).px(x + 2, 11, C.metalDk).px(x + leafW - 3, 11, C.metalDk);
        }
        if (d.kind === 'locked' || d.kind === 'exit') {
          p.rect(x + leafW / 2 - 1, 6, 3, 4, C.brassDk).px(x + leafW / 2, 7, O);
        }
        if (d.kind === 'bolt') {
          p.rect(x + 2, 6, leafW - 4, 3, C.metal).box(x + 2, 6, leafW - 4, 3, O).rect(x + leafW / 2 - 1, 5, 3, 2, C.metalLt);
        }
      }
    }
    return p.done();
  }
  // vertical door (in an E/W wall): seen edge-on, 16 wide x 16(+4) tall
  const p = new Painter(16, 24);
  if (d.kind === 'open' || d.kind === 'arch') {
    p.rect(0, 0, 16, 2, C.panelLt).rect(0, 18, 16, 2, C.panelLt);
    return p.done();
  }
  if (d.kind === 'secret' && !d.revealed) return p.done();
  if (open) {
    p.rect(0, 0, 16, 2, C.panelLt).rect(0, 18, 16, 2, C.panelLt);
    p.panel(5, 0, 6, 8, C.panel, O, C.panelLt, C.panelDk);
    return p.done();
  }
  if (d.kind === 'cell' || d.kind === 'gate') {
    p.rect(0, 0, 16, 20, '#0b0810');
    for (let y = 1; y < 20; y += 4) p.rect(0, y, 16, 2, C.bars);
    p.rect(7, 0, 2, 20, C.barsLt);
    return p.done();
  }
  p.panel(4, -4, 8, 24, C.panel, O, C.panelLt, C.panelDk);
  p.box(6, 1, 4, 6, C.panelDk).box(6, 10, 4, 6, C.panelDk);
  p.rect(9, 8, 2, 2, C.brass);
  if (d.kind === 'boarded') {
    p.rect(2, 4, 12, 3, C.woodPale).box(2, 4, 12, 3, O).rect(2, 12, 12, 3, C.woodPale).box(2, 12, 12, 3, O);
  }
  if (d.kind === 'locked' || d.kind === 'exit') p.rect(7, 8, 3, 3, C.brassDk).px(8, 9, O);
  if (d.kind === 'bolt') p.rect(5, 8, 6, 3, C.metal).box(5, 8, 6, 3, O);
  return p.done();
}

export function drawDoorEnt(ctx: CanvasRenderingContext2D, d: DoorEnt, offX: number, offY: number): void {
  if (d.kind === 'secret' && !d.revealed) return;
  const spr = doorSprite(d, d.open);
  const shake = !d.open && d.anim > 0 ? Math.round(Math.sin(d.anim * 70)) : 0;
  if (d.orient === 'h') drawRaster(ctx, spr, d.x * TILE - offX + shake, d.y * TILE - offY);
  else drawRaster(ctx, spr, d.x * TILE - offX, d.y * TILE - 4 - offY + shake);
}
